/**
 * PayPal checkout is a top-window redirect to approvalURL.
 * Capture happens on the API (GET /api/v1/paypal). The SPA only lands on callbackUrl.
 *
 * Platform builds PayPal's technical return as `{callbackUrl origin}/api/v1/paypal`.
 * callbackUrl MUST be on the app origin that proxies the API (vaiu-app), never the
 * embed parent host — that host has no /api/v1/paypal.
 *
 * Embedded iframe: open a named tab on the user click, send it to PayPal, return
 * to vaiu.app, then BroadcastChannel the result to the original iframe.
 * Full-page (not framed): same-window location.assign.
 */

export const VAIU_PAYPAL_TAB_NAME = 'vaiuPaypalFunding';
export const VAIU_PAYPAL_CHANNEL = 'vaiu-paypal-funding';
export const PAYPAL_SESSION_PARAM = 'paypalSession';
export const PAYPAL_TAB_PARAM = 'paypalTab';

export function isUnfundedTokenStatus(status?: string | null): boolean {
  return String(status || '').toUpperCase() === 'UNFUNDED';
}

export function paypalApprovalUrl(
  data: { approvalURL?: string; approvalUrl?: string } | null | undefined
): string {
  return String(data?.approvalURL || data?.approvalUrl || '');
}

export function isFramedWindow(win: Window = window): boolean {
  try {
    return Boolean(win.top && win.top !== win);
  } catch {
    return true;
  }
}

export function toAbsolutePayPalUrl(approvalURL: string, win: Window = window): string {
  return new URL(String(approvalURL || ''), win.location.href).toString();
}

export function createPayPalFundingSessionId(): string {
  const rand =
    typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  return `pp_${rand}`;
}

/** Named tab, no size features — a tab, not a popup. Call during the user click. */
export function openPayPalFundingTab(win: Window = window): Window | null {
  try {
    return win.open('about:blank', VAIU_PAYPAL_TAB_NAME);
  } catch {
    return null;
  }
}

export function navigatePayPalFundingTab(
  tab: Window,
  approvalURL: string,
  win: Window = window
): void {
  tab.location.assign(toAbsolutePayPalUrl(approvalURL, win));
}

export function closePayPalFundingTab(tab: Window | null | undefined): void {
  try {
    tab?.close();
  } catch {
    /* ignore */
  }
}

export type PayPalFundingBroadcast = {
  type: 'vaiu-paypal-funded' | 'vaiu-paypal-cancelled';
  sessionId: string;
  tokenKey?: string;
};

export function publishPayPalFundingResult(payload: PayPalFundingBroadcast): void {
  if (typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(VAIU_PAYPAL_CHANNEL);
  channel.postMessage(payload);
  channel.close();
}

export function subscribePayPalFundingResult(
  handler: (payload: PayPalFundingBroadcast) => void
): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    return () => undefined;
  }
  const channel = new BroadcastChannel(VAIU_PAYPAL_CHANNEL);
  const onMessage = (event: MessageEvent<PayPalFundingBroadcast>) => {
    const data = event.data;
    if (!data || (data.type !== 'vaiu-paypal-funded' && data.type !== 'vaiu-paypal-cancelled')) {
      return;
    }
    handler(data);
  };
  channel.addEventListener('message', onMessage);
  return () => {
    channel.removeEventListener('message', onMessage);
    channel.close();
  };
}

export function isPayPalFundingReturnTab(search: string): boolean {
  return new URLSearchParams(search).get(PAYPAL_TAB_PARAM) === '1';
}

export function readPayPalSessionId(search: string): string {
  return new URLSearchParams(search).get(PAYPAL_SESSION_PARAM) || '';
}

/**
 * Full-page only: same-window assign. Embeds should openPayPalFundingTab instead.
 */
export function goToPayPal(approvalURL: string | null | undefined, win: Window = window): boolean {
  const raw = String(approvalURL || '');
  if (!raw) return false;
  const url = toAbsolutePayPalUrl(raw, win);
  win.location.assign(url);
  return true;
}

/**
 * Landing URL after the API captures. Always this app's origin so PayPal return
 * `{origin}/api/v1/paypal` hits the API proxy, not the embed parent.
 */
export function resolvePayPalCallbackBaseUrl(options?: {
  returnUrl?: string;
  win?: Window;
}): string {
  const win = options?.win ?? (typeof window !== 'undefined' ? window : undefined);
  const appHref = win?.location.href || '';
  const appOrigin = win?.location.origin || '';

  if (options?.returnUrl) {
    try {
      const resolved = new URL(options.returnUrl, appHref || 'https://vaiu.app');
      if (!appOrigin || resolved.origin === appOrigin) {
        return resolved.toString();
      }
    } catch {
      /* foreign or invalid returnUrl — use the app URL */
    }
  }

  return appHref;
}

export function buildPayPalCallbackUrl(options?: {
  returnUrl?: string;
  context?: string;
  cancelled?: boolean;
  sessionId?: string;
  paypalTab?: boolean;
  win?: Window;
}): string {
  const base = resolvePayPalCallbackBaseUrl(options);
  if (!base) return '';
  const href =
    options?.win?.location.href ?? (typeof window !== 'undefined' ? window.location.href : undefined);
  const url = new URL(base, href || 'https://vaiu.app');
  url.searchParams.set('paypal', options?.cancelled ? 'cancelled' : 'complete');
  if (options?.context) {
    url.searchParams.set('context', options.context);
  }
  if (options?.sessionId) {
    url.searchParams.set(PAYPAL_SESSION_PARAM, options.sessionId);
  }
  if (options?.paypalTab) {
    url.searchParams.set(PAYPAL_TAB_PARAM, '1');
  }
  return url.toString();
}
